import { z } from 'zod';
import { DataSchedulingPlannedPullsState } from 'libs/end-to-end/src/types/databases/scheduling/database-schedule-state';
import { DataSchedulingPrepareNewRevisionState } from 'libs/end-to-end/src/types/databases/scheduling/database-schedule-state';
import { DataSchedulingRunTransformState } from 'libs/end-to-end/src/types/databases/scheduling/database-schedule-state';
import { DataSchedulingSetupState } from 'libs/end-to-end/src/types/databases/scheduling/database-schedule-state';
import { DataSchedulingValidateNewRevisionState } from 'libs/end-to-end/src/types/databases/scheduling/database-schedule-state';
import { DataSchedulingValidateTopLevelAnalysisState } from 'libs/end-to-end/src/types/databases/scheduling/database-schedule-state';
import { guardExhaustive } from 'libs/end-to-end/src/generic';
import { DatabaseScheduleRunStageInformation } from 'libs/end-to-end/src/types/databases/scheduling/database-schedule-run-stage';

export const DatabaseScheduleRunStatus = z.enum([
  'Pending',
  'Running',
  'Success',
  'Failed',
]);

export type DatabaseScheduleRunStatus = z.infer<
  typeof DatabaseScheduleRunStatus
>;

export const DatabaseScheduleRun = z.object({
  organizationId: z.string(),
  databaseId: z.string(),
  scheduleId: z.string(),
  runId: z.string(),
  scheduledStartEpoch: z.number().optional(),
  startedAtEpoch: z.number(),
  endedAtEpoch: z.number().nullable(),
  status: DatabaseScheduleRunStatus,
  stages: z.array(DatabaseScheduleRunStageInformation),
  setupState: DataSchedulingSetupState.nullable(),
  producePullIterationsState: DataSchedulingPlannedPullsState.nullable(),
  setupTransformState: DataSchedulingRunTransformState.nullable(),
  prepareNewRevisionState: DataSchedulingPrepareNewRevisionState.nullable(),
  validateNewRevisionState: DataSchedulingValidateNewRevisionState.nullable(),
  validateTopLevelAnalysisState:
    DataSchedulingValidateTopLevelAnalysisState.nullable(),
  errorReason: z.string().nullable(),
  sfnExecutionArn: z.string().optional(),
});

export type DatabaseScheduleRun = z.infer<typeof DatabaseScheduleRun>;

export const DatabaseScheduleRunWithSfnExecutionArnUrl =
  DatabaseScheduleRun.extend({
    sfnExecutionArnUrl: z.string().nullable(),
  });

export type DatabaseScheduleRunWithSfnExecutionArnUrl = z.infer<
  typeof DatabaseScheduleRunWithSfnExecutionArnUrl
>;

export const GetDatabaseScheduleRunsOutput = z.array(
  DatabaseScheduleRunWithSfnExecutionArnUrl,
);

export type GetDatabaseScheduleRunsOutput = z.infer<
  typeof GetDatabaseScheduleRunsOutput
>;

const DatabaseScheduleRuns = z.array(DatabaseScheduleRun);

type DatabaseScheduleRuns = z.infer<typeof DatabaseScheduleRuns>;

export const DatabaseScheduleRunForPatch = DatabaseScheduleRun.pick({
  status: true,
  endedAtEpoch: true,
  setupState: true,
  producePullIterationsState: true,
  setupTransformState: true,
  prepareNewRevisionState: true,
  validateNewRevisionState: true,
  validateTopLevelAnalysisState: true,
  errorReason: true,
  sfnExecutionArn: true,
  stages: true,
}).partial();

export type DatabaseScheduleRunForPatch = z.infer<
  typeof DatabaseScheduleRunForPatch
>;

export const isThereARunPendingOrRunning = (
  runs: DatabaseScheduleRuns,
): boolean => {
  const mostRecentRun = runs
    .sort((a, b) => b.startedAtEpoch - a.startedAtEpoch)
    .at(0);

  if (!mostRecentRun) {
    return false;
  }

  if (
    mostRecentRun.status === 'Pending' ||
    mostRecentRun.status === 'Running'
  ) {
    return true;
  } else if (
    mostRecentRun.status === 'Success' ||
    mostRecentRun.status === 'Failed'
  ) {
    return false;
  } else {
    throw guardExhaustive(mostRecentRun.status);
  }
};
